import { formatMoney } from '@theme/money-formatting';

/** @type {Map<string, Promise<string>>} */
const sectionCache = new Map();
const CACHE_MAX_SIZE = 100;

class VariantPicker extends HTMLElement {
  #abortController;
  #productData;
  #observer;

  connectedCallback() {
    this.addEventListener('change', this.#handleChange);
    this.#prefetchVariants();
    this.#observeStickyCta();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#handleChange);
    this.#observer?.disconnect();
  }

  #observeStickyCta() {
    this.#observer?.disconnect();
    const addToCart = this.querySelector('[data-add-to-cart]');
    const stickyCta = this.querySelector('[data-sticky-cta]');
    if (!addToCart || !stickyCta) return;
    this.#observer = new IntersectionObserver(([entry]) => {
      stickyCta.classList.toggle('translate-y-0!', !entry.isIntersecting);
    });
    this.#observer.observe(addToCart);
  }

  get productData() {
    if (!this.#productData) {
      try {
        this.#productData = JSON.parse(this.dataset.productJson);
      } catch {
        this.#productData = null;
      }
    }
    return this.#productData;
  }

  #handleChange = (e) => {
    if (!e.target.matches('input[type="radio"][name^="options["]')) return;
    this.fetchUpdatedSection(this.buildRequestUrl());
    this.#updateUrl();
  };

  #updateUrl() {
    if (this.dataset.templateProductMatch !== 'true') return;

    const variant = this.#getSelectedVariant();
    const url = new URL(window.location.href);

    if (variant) {
      url.searchParams.set('variant', variant.id);
    } else {
      url.searchParams.delete('variant');
    }

    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  }

  buildRequestUrl() {
    const url = new URL(this.dataset.productUrl, window.location.origin);

    if (this.selectedOptionsValues.length) {
      url.searchParams.set('option_values', this.selectedOptionsValues.join(','));
    }

    url.searchParams.set('sections', 'product-options');

    return url.toString();
  }

  fetchUpdatedSection(requestUrl) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const signal = this.#abortController.signal;

    const cached = sectionCache.get(requestUrl);

    const jsonPromise = cached
      ? cached.then((text) => JSON.parse(text))
      : fetch(requestUrl, { signal })
          .then((response) => {
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return response.json();
          });

    jsonPromise
      .then((json) => {
        if (signal.aborted) return;
        const sectionHtml = json['product-options'];
        if (!sectionHtml) return;

        this.updateVariantPicker(sectionHtml);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        console.error(error);
      });
  }

  #saveGiftCardState() {
    const form = this.querySelector('gift-card-form');
    if (!form) return null;

    const checkbox = form.querySelector('[data-send-as-gift]');
    if (!checkbox?.checked) return null;

    return {
      email: form.querySelector('[data-gift-card-email]')?.value || '',
      name: form.querySelector('[data-gift-card-name]')?.value || '',
      message: form.querySelector('[data-gift-card-message]')?.value || '',
      date: form.querySelector('[data-gift-card-date]')?.value || '',
    };
  }

  #restoreGiftCardState(state) {
    if (!state) return;

    const form = this.querySelector('gift-card-form');
    if (!form) return;

    const checkbox = form.querySelector('[data-send-as-gift]');
    if (checkbox) {
      checkbox.checked = true;
      checkbox.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const email = form.querySelector('[data-gift-card-email]');
    if (email) email.value = state.email;

    const name = form.querySelector('[data-gift-card-name]');
    if (name) name.value = state.name;

    const message = form.querySelector('[data-gift-card-message]');
    if (message) message.value = state.message;

    const date = form.querySelector('[data-gift-card-date]');
    if (date) {
      date.value = state.date;
      if (state.date) date.classList.remove('text-grey-5');
    }
  }

  updateVariantPicker(html) {
    const fragment = new DOMParser().parseFromString(html, 'text/html');
    const newContent = fragment.querySelector('variant-picker');

    if (!newContent) return;

    const prevLowStock = this.querySelector('[data-low-stock]')?.dataset.lowStock;
    const giftCardState = this.#saveGiftCardState();

    this.innerHTML = newContent.innerHTML;
    this.#restoreGiftCardState(giftCardState);
    this.#updatePrice();
    this.#observeStickyCta();

    const el = this.querySelector('[data-low-stock]');
    if (el && el.dataset.lowStock !== prevLowStock) {
      const target = el.dataset.lowStock;
      el.dataset.lowStock = prevLowStock;
      requestAnimationFrame(() => {
        el.dataset.lowStock = target;
      });
    }
  }

  #updatePrice() {
    const variant = this.#getSelectedVariant();
    if (!variant) return;

    const section = this.closest('.shopify-section');
    if (!section) return;

    const priceEls = section.querySelectorAll('[data-price]');
    const compareEls = section.querySelectorAll('[data-compare-price]');
    if (!priceEls.length) return;

    const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;

    priceEls.forEach((el) => {
      el.textContent = this.#formatPrice(variant.price);
      el.classList.toggle('text-sale', onSale);
    });

    compareEls.forEach((el) => {
      el.textContent = onSale ? this.#formatPrice(variant.compare_at_price) : '';
      el.classList.toggle('hidden', !onSale);
    });
  }

  #getSelectedVariant() {
    if (!this.productData?.variants) return null;
    const selectedOptions = this.#getSelectedOptionNames();
    if (!selectedOptions.length) return null;

    return this.productData.variants.find((variant) =>
      selectedOptions.every((value, i) => variant[`option${i + 1}`] === value)
    );
  }

  #getSelectedOptionNames() {
    return Array.from(this.querySelectorAll('input[type="radio"]:checked'))
      .map((input) => input.getAttribute('data-value'));
  }

  #formatPrice(cents) {
    if (cents == null) return '';
    const formatted = formatMoney(cents, Theme.moneyFormat, Theme.currency);
    return formatted.replace(/\.00$/, '');
  }

  #prefetchVariants() {
    const radios = this.querySelectorAll('input[type="radio"]');
    const selectedByName = new Map();

    for (const radio of radios) {
      if (radio.checked) selectedByName.set(radio.name, radio.value);
    }

    const radiosByName = new Map();
    for (const radio of radios) {
      if (!radiosByName.has(radio.name)) radiosByName.set(radio.name, []);
      radiosByName.get(radio.name).push(radio);
    }

    for (const [name, groupRadios] of radiosByName) {
      for (const radio of groupRadios) {
        if (radio.checked) continue;

        const optionValues = [];
        for (const [n, selectedValue] of selectedByName) {
          optionValues.push(n === name ? radio.value : selectedValue);
        }

        const url = new URL(this.dataset.productUrl, window.location.origin);
        url.searchParams.set('option_values', optionValues.join(','));
        url.searchParams.set('sections', 'product-options');

        const urlString = url.toString();

        if (!sectionCache.has(urlString)) {
          const promise = fetch(urlString, { priority: 'low' })
            .then((response) => {
              if (!response.ok) throw new Error(`HTTP ${response.status}`);
              return response.text();
            });

          sectionCache.set(urlString, promise);

          if (sectionCache.size > CACHE_MAX_SIZE) {
            sectionCache.delete(sectionCache.keys().next().value);
          }
        }
      }
    }
    
  }

  get selectedOptionsValues() {
    const selectedOptions = Array.from(this.querySelectorAll('input[type="radio"]:checked'));

    return selectedOptions.map((option) => {
      const { value } = option;

      if (!value) throw new Error('No option value ID found');

      return value;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
